import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import TrainingService from '../services/TrainingService';

const getErrorMessage = (error, fallbackMessage) => (
  error?.body?.message ||
  error?.body?.error ||
  error?.message ||
  fallbackMessage
);

const TrainingConfirmationPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const enrollmentId = searchParams.get('enrollmentId');
  const accessToken = searchParams.get('accessToken') || '';
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!enrollmentId) {
        setError('Missing enrollment reference.');
        setLoading(false);
        return;
      }

      try {
        const data = await TrainingService.getEnrollment(enrollmentId, { accessToken });
        if (!active) return;

        setEnrollment(data);
      } catch (loadError) {
        if (!active) return;
        setError(getErrorMessage(loadError, 'We could not load the booking details right now.'));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [accessToken, enrollmentId]);

  useEffect(() => {
    if (!searchParams.get('accessToken')) return;

    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('accessToken');
      return next;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  const statusAlert = useMemo(() => {
    if (!enrollment) return null;
    if (enrollment.status === 'paid') {
      return { severity: 'success', message: 'Payment received. Your training booking is confirmed.' };
    }
    if (enrollment.status === 'pending_payment') {
      return { severity: 'warning', message: 'Your booking exists, but payment is still incomplete.' };
    }
    return null;
  }, [enrollment]);

  if (loading) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert data-testid="training-confirmation-loading" severity="info">Loading booking confirmation...</Alert>
      </Container>
    );
  }

  return (
    <Box data-testid="training-confirmation-page" sx={{ py: { xs: 5, md: 8 }, background: 'linear-gradient(180deg, rgba(29,175,90,0.08), transparent 30%)' }}>
      <Container maxWidth="md">
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Stack data-testid="training-confirmation-header" direction="row" spacing={1.5} alignItems="center">
                <CheckCircleIcon color={enrollment?.status === 'paid' ? 'success' : 'warning'} />
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {enrollment?.status === 'paid' ? 'Booking confirmed' : 'Booking update'}
                </Typography>
              </Stack>

              {error ? <Alert data-testid="training-confirmation-error" severity="warning">{error}</Alert> : null}
              {statusAlert ? <Alert data-testid="training-confirmation-status-alert" severity={statusAlert.severity}>{statusAlert.message}</Alert> : null}

              {enrollment ? (
                <>
                  <Typography data-testid="training-confirmation-booking-reference" variant="body1" color="text.secondary">
                    Booking reference <strong>{enrollment.bookingReference}</strong>
                  </Typography>
                  <Divider />
                  <Stack data-testid="training-confirmation-details" spacing={1}>
                    <Typography data-testid="training-confirmation-course"><strong>Course:</strong> {enrollment.courseId}</Typography>
                    <Typography data-testid="training-confirmation-session"><strong>Session:</strong> {enrollment.sessionId}</Typography>
                    <Typography data-testid="training-confirmation-name"><strong>Name:</strong> {enrollment.firstName} {enrollment.lastName}</Typography>
                    <Typography data-testid="training-confirmation-email"><strong>Email:</strong> {enrollment.email}</Typography>
                    <Typography data-testid="training-confirmation-status"><strong>Status:</strong> {enrollment.status}</Typography>
                  </Stack>
                </>
              ) : null}

              <Alert severity="info">
                If you do not already have a completed jobseeker profile, you can finish it next to make future bookings faster.
              </Alert>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button component={RouterLink} to="/onboarding" variant="contained">
                  Create or complete profile
                </Button>
                <Button component={RouterLink} to="/training" variant="outlined">
                  Back to training
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default TrainingConfirmationPage;
